import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/app/context/AuthContext";
import { useUserScopedQueryKey } from "@/app/apiServices/queryScope";
import { activateGoogleAuthenticator, changePassword, changeTwoFactor, completeLoginRecovery, confirmAuthenticatorActivation, deactivateAuthenticator, getAuthHistory, recoverAuthenticatorFromWithdrawal, sendLoginRecoveryOtp, sendTwoFactorOtp, startAuthenticatorActivation, switchTwoFactorMethod, verifyGoogleAuthenticator, verifyCurrentPassword, verifyLoginRecoveryOtp } from "./apiCalls";
import { useFeatureFlags } from "@/app/apiServices/featureFlags/service";
import { isCryptoWithdrawHoldActive, twoFactorStateFromProfile } from "./helpers";
import { SECURITY_QUERY_KEYS, SECURITY_QUERY_OPTIONS } from "./constants";
import type { AuthHistoryData, AuthHistoryParams } from "@/app/components/Dashboard/Security/security.interface";
export const useAuthHistoryQuery = params => {
  const $ = _c(8);
  const {
    token,
    user
  } = useAuth();
  let t0;
  if ($[0] !== params) {
    t0 = SECURITY_QUERY_KEYS.authHistory(params);
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const queryKey = useUserScopedQueryKey(t0);
  let t1;
  if ($[2] !== params) {
    t1 = () => getAuthHistory(params);
    $[2] = params;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = Boolean(token && user);
  let t3;
  if ($[4] !== queryKey || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey,
      queryFn: t1,
      enabled: t2,
      select: _temp,
      ...SECURITY_QUERY_OPTIONS
    };
    $[4] = queryKey;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
};
export const useChangePassword = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationKey: SECURITY_QUERY_KEYS.changePassword,
      mutationFn: changePassword
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useVerifyCurrentPassword = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationKey: SECURITY_QUERY_KEYS.verifyCurrentPassword,
      mutationFn: verifyCurrentPassword
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useActivateGoogleAuthenticator = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationKey: SECURITY_QUERY_KEYS.activateGoogleAuthenticator,
      mutationFn: activateGoogleAuthenticator
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useVerifyGoogleAuthenticator = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationKey: SECURITY_QUERY_KEYS.verifyGoogleAuthenticator,
      mutationFn: verifyGoogleAuthenticator
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useChangeTwoFactor = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationKey: SECURITY_QUERY_KEYS.changeTwoFactor,
      mutationFn: changeTwoFactor
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useTwoFactorState = () => {
  const $ = _c(4);
  const {
    user
  } = useAuth();
  let t0;
  if ($[0] !== user) {
    t0 = twoFactorStateFromProfile(user);
    $[0] = user;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = {
      data: t0
    };
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
export const useSendTwoFactorOtp = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationKey: SECURITY_QUERY_KEYS.sendTwoFactorOtp,
      mutationFn: sendTwoFactorOtp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useStartAuthenticatorActivation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationKey: SECURITY_QUERY_KEYS.startAuthenticatorActivation,
      mutationFn: startAuthenticatorActivation
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};

// Mutations below change the 2FA settings, which live on the profile.
const useTwoFactorStateMutation = (mutationKey, mutationFn) => {
  const $ = _c(6);
  const {
    refreshUser
  } = useAuth();
  let t0;
  if ($[0] !== refreshUser) {
    t0 = () => refreshUser();
    $[0] = refreshUser;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== mutationFn || $[3] !== mutationKey || $[4] !== t0) {
    t1 = {
      mutationKey,
      mutationFn,
      onSuccess: t0
    };
    $[2] = mutationFn;
    $[3] = mutationKey;
    $[4] = t0;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return useMutation(t1);
};
export const useConfirmAuthenticatorActivation = () => {
  return useTwoFactorStateMutation(SECURITY_QUERY_KEYS.confirmAuthenticatorActivation, confirmAuthenticatorActivation);
};
export const useSwitchTwoFactorMethod = () => {
  return useTwoFactorStateMutation(SECURITY_QUERY_KEYS.switchTwoFactorMethod, switchTwoFactorMethod);
};
export const useDeactivateAuthenticator = () => {
  return useTwoFactorStateMutation(SECURITY_QUERY_KEYS.deactivateAuthenticator, deactivateAuthenticator);
};

// The backend revokes every session here and the caller logs out, so nothing is refreshed.
export const useRecoverAuthenticatorFromWithdrawal = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationKey: SECURITY_QUERY_KEYS.recoverFromWithdrawal,
      mutationFn: recoverAuthenticatorFromWithdrawal
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};

// Login recovery runs before a session exists, so it uses plain, unscoped mutations.
export const useSendLoginRecoveryOtp = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationKey: SECURITY_QUERY_KEYS.sendLoginRecoveryOtp,
      mutationFn: sendLoginRecoveryOtp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useVerifyLoginRecoveryOtp = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationKey: SECURITY_QUERY_KEYS.verifyLoginRecoveryOtp,
      mutationFn: verifyLoginRecoveryOtp,
      meta: {
        showError: false
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useCompleteLoginRecovery = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationKey: SECURITY_QUERY_KEYS.completeLoginRecovery,
      mutationFn: completeLoginRecovery,
      meta: {
        showError: false
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};

/** Crypto withdrawal is blocked while this is true (PRD 13); never affects Toman or other actions. */
export const useCryptoWithdrawHold = () => {
  const $ = _c(5);
  const {
    data
  } = useTwoFactorState();
  const {
    twoFactorV3
  } = useFeatureFlags();
  let t0;
  if ($[0] !== data?.cryptoWithdrawHoldUntil || $[1] !== twoFactorV3) {
    t0 = Boolean(twoFactorV3) && isCryptoWithdrawHoldActive(data?.cryptoWithdrawHoldUntil);
    $[0] = data?.cryptoWithdrawHoldUntil;
    $[1] = twoFactorV3;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== t0) {
    t1 = {
      isActive: t0
    };
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
function _temp(data) {
  return data.message ?? {};
}